import { Skin } from '@core/interfaces/skin.interface';
import { normalizeSkinImage } from '@core/utils/skins.util';
import { WIN_TOAST_GAMES, WinToastGame } from '@core/constants/win-toast.constants';
import { WinToastInput } from './win-toast.service';

/**
 * The only place the BE `offer_sent` payload shape is defined.
 *
 * Contract confirmed by the BE owner (2026-10-28, live 2026-10-31); the single
 * written copy is `docs/integration/guide/shared-contract.md` → "`offer_sent`".
 * Everything downstream (`WinToastService`, toast UI, `WithdrawSentModal`)
 * only sees the results of `classifyOfferSent()`.
 */
export interface WinToastPushPayload {
  /** Recipient steamid. Pushes whose `userid` is not the session steamid are dropped. */
  userid: string;
  /** `"Store"` for a withdrawal; otherwise `"Coinflip" | "Upgrader" | "King" | "Jackpot"` (aliases accepted, see `GAME_ALIASES`). */
  game: string;
  /** Offer / round hash, always present. */
  hash: string;
  /** Present for every game, absent for `"Store"`. */
  gameid?: number | string;
  /** King rounds only. */
  roundid?: number;
  /** Jackpot only. */
  potid?: number;
  /** Items in the offer. Missing images / colors are normalised like elsewhere. */
  skins?: WinToastPushSkin[];
  /**
   * IGNORED (BE owner, 2026-10-31: "ignore the value and add up the sum of the
   * skins" — the first sample had 3.14 against 2.24 of skins). The FE always
   * shows Σ `skins[].price`. Kept here so nobody re-adds a preference for it.
   */
  value?: number;
  /** Steam trade offer id (string in production). Builds the offer URL. */
  offerid?: string | number;
  /** Direct offer URL — not sent today, accepted if it ever is. */
  offerUrl?: string;
}

/** Superset of what the BE sends for a skin in won/bet/offer payloads today. */
export interface WinToastPushSkin {
  assetid?: string | number;
  id?: string | number;
  name?: string;
  market_hash_name?: string;
  image?: string;
  icon_url?: string;
  color?: string;
  name_color?: string;
  price?: number | string;
  type?: string;
  effect?: string | null;
}

/** Data for `ModalService.openModal('WithdrawSentModal', …)` (no `balancetype`: no longer sent). */
export interface WithdrawSentModalData {
  offerid: string;
  hash: string;
  /** Σ `skins[].price`, same rule as the toast. */
  value: number;
  /** Raw skins — the modal normalises images itself. */
  skins: WinToastPushSkin[];
}

export type OfferSentResult =
  | { kind: 'toast'; input: WinToastInput }
  | { kind: 'store'; data: WithdrawSentModalData };

/** Spellings the BE uses for the same game in other events → toast game key. */
const GAME_ALIASES: Record<string, WinToastGame> = {
  cf: 'coinflip',
  coinflip: 'coinflip',
  upgrade: 'upgrader',
  upgrader: 'upgrader',
  upgrader_new: 'upgrader',
  king: 'king',
  king_new: 'king',
  jackpot: 'jackpot',
  jackpot_hr: 'jackpot',
  highroller: 'jackpot',
};

const STORE_GAME = 'store';

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

export function sumSkinPrices(skins: { price: number }[]): number {
  return round2(skins.reduce((sum, s) => sum + (s.price ?? 0), 0));
}

export function normalizeWinToastGame(game: unknown): WinToastGame | null {
  if (typeof game !== 'string') return null;
  const key = game.trim().toLowerCase();
  const mapped = GAME_ALIASES[key];
  return mapped && (WIN_TOAST_GAMES as readonly string[]).includes(mapped) ? mapped : null;
}

function isStoreGame(game: unknown): boolean {
  return typeof game === 'string' && game.trim().toLowerCase() === STORE_GAME;
}

function hasOfferId(offerid: unknown): offerid is string | number {
  return offerid !== undefined && offerid !== null && offerid !== '';
}

function normalizeSkin(raw: WinToastPushSkin, index: number): Skin {
  const name = raw.market_hash_name || raw.name || '';
  const image = raw.image || raw.icon_url || '';
  return {
    assetid: String(raw.assetid ?? raw.id ?? `win-${index}`),
    name,
    image: normalizeSkinImage(image),
    color: raw.color || raw.name_color || '',
    price: Number(raw.price) || 0,
    ...(raw.type && { type: raw.type }),
    ...(raw.effect !== undefined && { effect: raw.effect }),
  };
}

/**
 * Recipient guard (hard rule, 2026-10-31). The push is for this session only
 * when `userid` is a string strictly equal to the session steamid. Dropped:
 * missing / non-string / foreign `userid` (the bot winner is `'-1'`) and every
 * push while logged out (`selfSteamid === null`, nothing to compare to).
 *
 * Drops warn once per page load, not once per push: if the push turns out to
 * be broadcast, every bot win would otherwise warn in every client.
 */
let recipientWarned = false;

export function isOfferForUser(raw: unknown, selfSteamid: string | null): boolean {
  const p = (raw ?? {}) as Partial<WinToastPushPayload>;
  let reason: string | null = null;
  if (selfSteamid === null) reason = 'not logged in';
  else if (typeof p.userid !== 'string') reason = 'userid missing or not a string';
  else if (p.userid !== selfSteamid) reason = 'userid is not the session steamid';

  if (reason === null) return true;
  if (!recipientWarned) {
    recipientWarned = true;
    console.warn(`[win-toast] offer_sent dropped (${reason}); further drops are silent`, raw);
  }
  return false;
}

/**
 * `offer_sent` → what to do with it. Order: recipient guard → Store branch →
 * toast mapper. Store runs before `mapWinToastPush()` because a Store push has
 * no `gameid`, which the mapper requires. `null` = drop (already warned).
 */
export function classifyOfferSent(
  raw: unknown,
  selfSteamid: string | null,
  opts: { devMock?: boolean } = {},
): OfferSentResult | null {
  if (!isOfferForUser(raw, selfSteamid)) return null;

  const p = (raw ?? {}) as Partial<WinToastPushPayload>;
  if (isStoreGame(p.game)) {
    if (!hasOfferId(p.offerid)) {
      console.warn('[win-toast] Store offer_sent without offerid, ignored', raw);
      return null;
    }
    const skins = Array.isArray(p.skins) ? p.skins : [];
    return {
      kind: 'store',
      data: {
        offerid: String(p.offerid),
        hash: String(p.hash ?? ''),
        value: sumSkinPrices(skins.map(normalizeSkin)),
        skins,
      },
    };
  }

  const input = mapWinToastPush(raw, opts);
  return input ? { kind: 'toast', input } : null;
}

/**
 * Game push → `WinToastService.show()` input, or `null` (with a console
 * warning) when the payload cannot be shown. Does not run the recipient guard
 * — go through `classifyOfferSent()` for a raw socket frame. `devMock` marks
 * toasts raised by the dev launcher so they stay distinguishable.
 */
export function mapWinToastPush(
  raw: unknown,
  opts: { devMock?: boolean } = {},
): WinToastInput | null {
  const p = (raw ?? {}) as Partial<WinToastPushPayload>;
  const game = normalizeWinToastGame(p.game);
  if (!game) {
    console.warn('[win-toast] push with unknown game, ignored', raw);
    return null;
  }
  if (p.gameid === undefined || p.gameid === null || p.gameid === '') {
    console.warn('[win-toast] push without gameid, ignored', raw);
    return null;
  }

  const resolvedOfferUrl =
    p.offerUrl ??
    (hasOfferId(p.offerid) ? `https://steamcommunity.com/tradeoffer/${p.offerid}/` : null);
  if (!resolvedOfferUrl) {
    console.warn('[win-toast] push without offerUrl/offerid, ignored', raw);
    return null;
  }

  const skins = Array.isArray(p.skins) ? p.skins.map(normalizeSkin) : [];

  return {
    game,
    gameId: typeof p.gameid === 'number' ? p.gameid : String(p.gameid),
    ...(typeof p.roundid === 'number' && { roundId: p.roundid }),
    ...(typeof p.potid === 'number' && { potid: p.potid }),
    skins,
    // Always the skins' sum — the BE `value` is ignored by contract.
    totalValue: sumSkinPrices(skins),
    offerUrl: resolvedOfferUrl,
    ...(hasOfferId(p.offerid) && { offerid: String(p.offerid) }),
    ...(opts.devMock && { devMock: true }),
  };
}
